import { useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  ButtonSpinner,
  cn,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  Input,
  NativeSelect,
  SearchInput,
  Skeleton,
  toast,
} from '@eureca/ui';
import { CheckIcon, ClockIcon, DownloadIcon, XIcon } from './icons';
import {
  stageDetailsQueryOptions,
  stageDetailsQueryKey,
  moduleStatusFor,
  phaseStatusFor,
  phaseScoreFor,
  candidateModuleProgress,
  moduleCompletionSummary,
  advanceCandidatesToPhase,
  addModuleWhitelist,
  removeModuleWhitelist,
  type StagePhaseStatus,
  type StepCandidate,
} from './stage-details-query';
import { downloadStageCandidatesXlsx, stageCandidatesFileName } from './stage-candidates-export';

interface StageDetailsSheetProps {
  /** Vaga (= jobId). */
  jobId: string;
  /** Etapa (job_step) cujos candidatos serão listados. */
  stepId: string | null;
  /** Módulo específico → detalhes de UM módulo. `null` = detalhes da ETAPA inteira. */
  moduleId: string | null;
  /** Nome do módulo ou da etapa, exibido no cabeçalho. */
  title: string;
  /**
   * Próxima etapa (id + nome) — habilita "Passar de fase" quando é a visão de
   * ETAPA (`moduleId` null) e existe uma próxima fase. Null = última fase / visão
   * de módulo → drawer read-only.
   */
  nextStepId?: string | null;
  nextStepTitle?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type StatusFilter = 'todos' | StagePhaseStatus;

/**
 * Drawer "Detalhes" da job-flow. Dois modos:
 *   - `moduleId` setado → estado do candidato NAQUELE módulo (+ nota) — read-only
 *     (Pendente/Em andamento/Concluído, derivado de `module_user_progress`);
 *   - `moduleId` null   → visão da ETAPA: estado LEDGER-driven (QA 2026-07-30 —
 *     Concluído/Em andamento/Reprovado, de `job_step_user_progress`; "pendente"
 *     saiu do contador — nunca ocorria nesta visão), nota quando a etapa tem
 *     módulo pontuado, e — quando há próxima fase — seleção múltipla +
 *     "Passar de fase" (curadoria do funil pelo consultor). Candidato REPROVADO
 *     (EU-552) não é selecionável — a movimentação é bloqueada no Core.
 *
 * Escopado pelo Core a quem está na fase. O FatorH mantém seu painel dedicado.
 */
export function StageDetailsSheet({
  jobId,
  stepId,
  moduleId,
  title,
  nextStepId,
  nextStepTitle,
  open,
  onOpenChange,
}: StageDetailsSheetProps) {
  const queryClient = useQueryClient();
  const query = useQuery(stageDetailsQueryOptions(jobId, stepId, open));
  const modules = useMemo(() => query.data?.modules ?? [], [query.data]);
  const candidates = useMemo(() => query.data?.candidates ?? [], [query.data]);

  const isPhase = moduleId === null;
  // A passagem de fase existe pra esta etapa (há uma próxima fase)...
  const advanceAvailable = isPhase && !!nextStepId;

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('todos');
  const [minScore, setMinScore] = useState('');
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [exporting, setExporting] = useState(false);

  // ...mas só passa de fase quem CONCLUIU. Então a seleção + o botão só aparecem
  // com o filtro em "Concluído" (garante que ninguém não-concluído é selecionado).
  const canAdvance = advanceAvailable && statusFilter === 'completed';

  // Régua híbrida da etapa (FR-13): ledger + "concluiu todos os módulos
  // visíveis". Oculto fica fora (whitelist não define conclusão da fase).
  const visibleModuleIds = useMemo(
    () => modules.filter((m) => !m.hidden).map((m) => m.id),
    [modules]
  );
  const statusOf = useMemo(
    () =>
      (c: StepCandidate): StagePhaseStatus =>
        moduleId ? moduleStatusFor(c, moduleId).status : phaseStatusFor(c, visibleModuleIds),
    [moduleId, visibleModuleIds]
  );
  const scoreOf = useMemo(
    () =>
      (c: StepCandidate): number | null => {
        if (isPhase) return phaseScoreFor(c);
        const raw = moduleStatusFor(c, moduleId as string).score;
        if (raw == null) return null;
        const n = Number(raw);
        return Number.isNaN(n) ? null : n;
      },
    [isPhase, moduleId]
  );

  // A etapa/módulo é pontuada? (algum candidato tem nota) → mostra coluna + filtro.
  const scored = useMemo(() => candidates.some((c) => scoreOf(c) !== null), [candidates, scoreOf]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const min = minScore.trim() === '' ? null : Number(minScore);
    return candidates
      .map((candidate) => ({ candidate, status: statusOf(candidate), score: scoreOf(candidate) }))
      .filter(({ candidate }) => matchesSearch(candidate, q))
      .filter(({ status }) => statusFilter === 'todos' || status === statusFilter)
      .filter(({ score }) => min === null || Number.isNaN(min) || (score !== null && score >= min));
  }, [candidates, statusOf, scoreOf, search, statusFilter, minScore]);

  const counts = useMemo(() => {
    const acc = { completed: 0, in_progress: 0, pending: 0, rejected: 0 };
    for (const c of candidates) acc[statusOf(c)] += 1;
    return acc;
  }, [candidates, statusOf]);
  // FR-15 — quantos concluíram cada módulo visível (toda a fase, como os contadores).
  const moduleSummary = useMemo(
    () => (isPhase ? moduleCompletionSummary(candidates, modules) : []),
    [isPhase, candidates, modules]
  );

  const advanceMutation = useMutation({
    mutationFn: (userIds: string[]) =>
      advanceCandidatesToPhase(jobId, nextStepId as string, userIds),
    onSuccess: (res) => {
      const skipped = res.ignored.length;
      toast.success(
        `${res.advanced.length} candidato(s) passaram para "${nextStepTitle}"${
          skipped ? `, ${skipped} ignorado(s) (sem inscrição ativa)` : ''
        }.`
      );
      setSelected(new Set());
      void queryClient.invalidateQueries({ queryKey: stageDetailsQueryKey(jobId, stepId ?? '') });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : 'Falha ao passar candidatos de fase'),
  });

  // Módulo OCULTO na visão de módulo → habilita o toggle de whitelist por
  // candidato (liberar tester a fazer o módulo sem desocultar pra vaga inteira).
  const moduleHidden = useMemo(
    () => (moduleId ? (modules.find((m) => m.id === moduleId)?.hidden ?? false) : false),
    [moduleId, modules]
  );
  const whitelistMutation = useMutation({
    mutationFn: ({ userId, add }: { userId: string; add: boolean }) =>
      add
        ? addModuleWhitelist(jobId, moduleId as string, [userId])
        : removeModuleWhitelist(jobId, moduleId as string, userId),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: stageDetailsQueryKey(jobId, stepId ?? '') }),
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Falha ao alterar o acesso'),
  });

  function toggle(userId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }
  /** Seleciona todos os concluídos visíveis (o conjunto natural de avanço).
   *  Reprovado fica fora — a movimentação dele é bloqueada no Core (EU-552). */
  function selectCompleted() {
    setSelected(
      new Set(
        rows
          .filter((r) => r.status === 'completed' && !r.candidate.rejected)
          .map((r) => r.candidate.userId)
      )
    );
  }
  const completedVisible = useMemo(
    () => rows.filter((r) => r.status === 'completed' && !r.candidate.rejected).length,
    [rows]
  );

  // AA-62 — exporta a lista VISÍVEL (busca + estado + nota mínima já aplicados):
  // o que se vê é o que se exporta. Dados já estão no cliente; sem backend.
  async function exportRows() {
    if (exporting || rows.length === 0) return;
    setExporting(true);
    try {
      // Visão de etapa: uma coluna por módulo (estado + nota) — feedback da
      // Déborah 04/09: "em que parte está e o resultado de cada parte".
      await downloadStageCandidatesXlsx(
        rows,
        { isPhase, scored, modules: isPhase ? modules : undefined },
        stageCandidatesFileName(title, statusFilter)
      );
      toast.success(`${rows.length} candidato(s) exportado(s).`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao exportar a lista');
    } finally {
      setExporting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className={cn(
          'gap-0 p-0',
          'lg:left-auto lg:right-0 lg:top-0 lg:bottom-0',
          'lg:h-dvh lg:max-h-dvh lg:w-full lg:max-w-[560px]',
          'lg:translate-x-0 lg:translate-y-0 lg:rounded-none',
          'flex flex-col',
          'data-[state=open]:animate-sheet-in-bottom data-[state=closed]:animate-sheet-out-bottom',
          'lg:data-[state=open]:animate-sheet-in-right lg:data-[state=closed]:animate-sheet-out-right'
        )}
      >
        <header className="flex flex-row items-start gap-1.5 px-6 pt-6 pb-3">
          <div className="flex flex-1 flex-col gap-1">
            <DialogTitle className="text-lg font-semibold leading-none tracking-tight text-foreground">
              {title || 'Detalhes'}
            </DialogTitle>
            <span className="text-xs font-medium uppercase leading-none tracking-wide text-muted-foreground">
              {moduleId ? 'Módulo' : 'Etapa'}
            </span>
          </div>
          <DialogClose asChild>
            <button
              type="button"
              aria-label="Fechar"
              className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-border/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <XIcon className="size-4" />
            </button>
          </DialogClose>
        </header>

        <div className="flex flex-1 flex-col overflow-hidden">
          <div className="px-6 pb-3">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Buscar por nome ou e-mail"
            />
          </div>

          <div className="flex flex-row flex-wrap items-center justify-between gap-2 px-6 pb-3">
            {/* Visão de etapa: reprovado no lugar de "pendente" (QA 2026-07-30 —
                pendente nunca ocorria aqui; na visão de módulo ele segue real). */}
            <p className="text-xs leading-4 text-muted-foreground">
              <span className="font-medium text-foreground">{counts.completed}</span> concluído
              {' · '}
              <span className="font-medium text-foreground">{counts.in_progress}</span> em andamento
              {' · '}
              {isPhase ? (
                <>
                  <span className="font-medium text-foreground">{counts.rejected}</span> reprovado
                </>
              ) : (
                <>
                  <span className="font-medium text-foreground">{counts.pending}</span> pendente
                </>
              )}
            </p>
            <div className="flex flex-row items-center gap-2">
              {scored ? (
                <Input
                  type="number"
                  inputMode="numeric"
                  aria-label="Nota mínima"
                  placeholder="Nota mín."
                  value={minScore}
                  onChange={(e) => setMinScore(e.target.value)}
                  className="w-24"
                />
              ) : null}
              <NativeSelect
                aria-label="Filtrar por estado"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                className="w-40"
              >
                <option value="todos">Todos</option>
                <option value="completed">Concluído</option>
                <option value="in_progress">Em andamento</option>
                {isPhase ? (
                  <option value="rejected">Reprovado</option>
                ) : (
                  <option value="pending">Pendente</option>
                )}
              </NativeSelect>
              <Button
                type="button"
                variant="outline"
                size="sm"
                title="Exportar a lista visível (.xlsx)"
                data-testid="stage-details-export"
                loading={exporting}
                disabled={exporting || query.isLoading || query.isError || rows.length === 0}
                onClick={() => void exportRows()}
              >
                <DownloadIcon />
                Exportar
              </Button>
            </div>
          </div>

          {/* FR-14/15 ("igual o RH faz"): conclusão POR MÓDULO — "Concluído" na
              etapa é a fase toda; aqui se vê em que módulo a fase está travando. */}
          {moduleSummary.length > 0 ? (
            <div className="flex flex-row flex-wrap items-center gap-x-3 gap-y-1 px-6 pb-3 text-[11px] leading-4 text-muted-foreground">
              <span className="uppercase tracking-wide">Concluíram</span>
              {moduleSummary.map(({ module, done }) => (
                <span key={module.id} className="inline-flex items-center gap-1">
                  <span className="font-medium text-foreground">{done}</span>
                  <span className="max-w-[180px] truncate" title={module.name}>
                    {module.name}
                  </span>
                </span>
              ))}
            </div>
          ) : null}

          {/* Curadoria do funil: só na visão de etapa quando há próxima fase. */}
          {canAdvance ? (
            <div className="flex flex-row flex-wrap items-center justify-between gap-2 border-y border-border bg-muted/40 px-6 py-2">
              <span className="text-xs text-muted-foreground">
                {selected.size > 0 ? (
                  <>
                    <span className="font-medium text-foreground">{selected.size}</span>{' '}
                    selecionado(s)
                  </>
                ) : (
                  'Selecione quem passa de fase'
                )}
              </span>
              <div className="flex flex-row items-center gap-2">
                {completedVisible > 0 ? (
                  <button
                    type="button"
                    onClick={selectCompleted}
                    className="text-xs font-medium text-primary transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-sm"
                  >
                    Selecionar concluídos ({completedVisible})
                  </button>
                ) : null}
                {selected.size > 0 ? (
                  <button
                    type="button"
                    onClick={() => setSelected(new Set())}
                    className="text-xs font-medium text-muted-foreground transition-opacity hover:opacity-80 focus-visible:outline-none rounded-sm"
                  >
                    Limpar
                  </button>
                ) : null}
              </div>
            </div>
          ) : advanceAvailable ? (
            // Feature disponível, mas só se passa quem concluiu → guia o consultor
            // a filtrar por "Concluído" (aí a seleção + o botão aparecem).
            <div className="flex flex-row flex-wrap items-center gap-1.5 border-y border-border bg-muted/40 px-6 py-2 text-xs text-muted-foreground">
              <span>Filtre por</span>
              <button
                type="button"
                onClick={() => setStatusFilter('completed')}
                className="font-medium text-primary transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-sm"
              >
                Concluído
              </button>
              <span>para selecionar quem passa de fase.</span>
            </div>
          ) : null}

          <div className="flex flex-1 flex-col gap-2 overflow-y-auto px-6 pb-4 pt-3">
            {query.isLoading ? (
              <ListSkeleton />
            ) : query.isError ? (
              <ErrorText />
            ) : rows.length === 0 ? (
              <EmptyText total={candidates.length} />
            ) : (
              rows.map(({ candidate, status, score }) => (
                <CandidateRow
                  key={candidate.userId}
                  candidate={candidate}
                  status={status}
                  score={score}
                  showAdvanced={isPhase}
                  moduleProgress={
                    isPhase ? candidateModuleProgress(candidate, visibleModuleIds) : null
                  }
                  selectable={canAdvance && !candidate.rejected}
                  selected={selected.has(candidate.userId)}
                  onToggle={() => toggle(candidate.userId)}
                  showWhitelist={moduleHidden}
                  whitelistOn={moduleId ? candidate.whitelistedModuleIds.includes(moduleId) : false}
                  whitelistPending={
                    whitelistMutation.isPending &&
                    whitelistMutation.variables?.userId === candidate.userId
                  }
                  onWhitelistToggle={() =>
                    whitelistMutation.mutate({
                      userId: candidate.userId,
                      add: !(moduleId ? candidate.whitelistedModuleIds.includes(moduleId) : false),
                    })
                  }
                />
              ))
            )}
          </div>

          {canAdvance && selected.size > 0 ? (
            <footer className="border-t border-border px-6 py-3">
              <Button
                type="button"
                className="w-full"
                disabled={advanceMutation.isPending}
                onClick={() => advanceMutation.mutate([...selected])}
              >
                {advanceMutation.isPending ? <ButtonSpinner /> : null}
                Passar {selected.size} para "{nextStepTitle}"
              </Button>
            </footer>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CandidateRow({
  candidate,
  status,
  score,
  showAdvanced,
  moduleProgress,
  selectable,
  selected,
  onToggle,
  showWhitelist,
  whitelistOn,
  whitelistPending,
  onWhitelistToggle,
}: {
  candidate: StepCandidate;
  status: StagePhaseStatus;
  score: number | null;
  showAdvanced: boolean;
  /** n/N módulos visíveis concluídos (visão de etapa) — FR-14. */
  moduleProgress: { done: number; total: number } | null;
  selectable: boolean;
  selected: boolean;
  onToggle: () => void;
  /** Módulo oculto → mostra o toggle de liberar acesso (whitelist). */
  showWhitelist: boolean;
  whitelistOn: boolean;
  whitelistPending: boolean;
  onWhitelistToggle: () => void;
}) {
  const { name, socialName, email, advancedByRecruiter } = candidate;
  // Nome social prevalece sobre o civil (EU-594) — mesmo critério do perfil.
  const primary = socialName ?? name ?? email ?? '—';
  // EU-594 — porta de entrada do Perfil do Candidato, preservando a área de
  // trabalho (`companyId`) da URL atual.
  const navigate = useNavigate();
  const { companyId } = useSearch({ strict: false }) as { companyId?: string };
  return (
    <label
      className={cn(
        'flex flex-row items-center gap-3 rounded-xl border bg-card p-3',
        selectable && 'cursor-pointer',
        selected ? 'border-primary ring-1 ring-primary/30' : 'border-border'
      )}
    >
      {selectable ? (
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          aria-label={`Selecionar ${primary}`}
          className="size-4 shrink-0 rounded border-border text-primary focus-visible:ring-2 focus-visible:ring-primary/40"
        />
      ) : null}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="truncate text-sm font-medium leading-none text-foreground">{primary}</p>
        {name && email ? (
          <p className="truncate text-xs leading-none text-muted-foreground">{email}</p>
        ) : null}
        <div className="flex flex-row flex-wrap items-center gap-1.5 pt-0.5">
          <StatusBadge status={status} />
          {moduleProgress && moduleProgress.total > 0 ? (
            <span className="text-[11px] leading-none text-muted-foreground">
              {moduleProgress.done}/{moduleProgress.total} módulos
            </span>
          ) : null}
          {showAdvanced && advancedByRecruiter && status !== 'rejected' ? (
            <span className="text-[11px] leading-none text-muted-foreground">
              avançado pelo recrutador
            </span>
          ) : null}
          {/* Reprovado em OUTRA fase (aqui a linha lê Concluído/Em andamento):
              sinaliza que o candidato não é mais movimentável. */}
          {showAdvanced && candidate.rejected && status !== 'rejected' ? (
            <span className="text-[11px] leading-none text-destructive">reprovado na vaga</span>
          ) : null}
          <button
            type="button"
            data-testid="stage-details-open-profile"
            onClick={(e) => {
              // O row é um <label> da seleção — sem preventDefault o clique
              // togglaria o checkbox junto (mesmo racional do botão whitelist).
              e.preventDefault();
              void navigate({
                to: '/candidatos/$userId',
                params: { userId: candidate.userId },
                search: { companyId },
              });
            }}
            className="rounded-sm text-[11px] font-medium leading-none text-primary transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            Acessar perfil completo
          </button>
        </div>
      </div>
      {score !== null ? (
        <div className="flex shrink-0 flex-col items-end">
          <span className="text-lg font-semibold leading-none text-foreground">{score}</span>
          <span className="text-[11px] uppercase leading-none text-muted-foreground">nota</span>
        </div>
      ) : null}
      {showWhitelist ? (
        <button
          type="button"
          disabled={whitelistPending}
          onClick={(e) => {
            e.preventDefault();
            onWhitelistToggle();
          }}
          aria-pressed={whitelistOn}
          title={
            whitelistOn ? 'Remover acesso ao módulo oculto' : 'Liberar acesso ao módulo oculto'
          }
          className={cn(
            'shrink-0 rounded-md px-2 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-50',
            whitelistOn
              ? 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
              : 'border border-border text-primary hover:bg-muted'
          )}
        >
          {whitelistPending ? '…' : whitelistOn ? 'Liberado ✓' : 'Liberar'}
        </button>
      ) : null}
    </label>
  );
}

function StatusBadge({ status }: { status: StagePhaseStatus }) {
  if (status === 'completed') {
    return (
      <Badge variant="secondary" className="gap-1">
        <CheckIcon className="size-3" />
        Concluído
      </Badge>
    );
  }
  if (status === 'in_progress') {
    return (
      <Badge variant="outline" className="gap-1">
        <ClockIcon className="size-3" />
        Em andamento
      </Badge>
    );
  }
  if (status === 'rejected') {
    return (
      <Badge variant="destructive" className="gap-1">
        <XIcon className="size-3" />
        Reprovado
      </Badge>
    );
  }
  return <Badge variant="outline">Pendente</Badge>;
}

function ListSkeleton() {
  return (
    <>
      {[0, 1, 2, 3].map((i) => (
        <Skeleton key={i} className="h-16 w-full rounded-xl" />
      ))}
    </>
  );
}

function ErrorText() {
  return (
    <p className="text-sm text-destructive">
      Falha ao carregar os candidatos. Feche e reabra o painel.
    </p>
  );
}

function EmptyText({ total }: { total: number }) {
  return (
    <p className="text-sm text-muted-foreground">
      {total === 0 ? 'Ninguém está nesta etapa ainda.' : 'Nenhum candidato com os filtros atuais.'}
    </p>
  );
}

function matchesSearch(c: StepCandidate, query: string): boolean {
  if (!query) return true;
  return `${c.socialName ?? ''} ${c.name ?? ''} ${c.email ?? ''}`.toLowerCase().includes(query);
}
